import { ENDPOINTS } from '@shared/utils/constant'
import { getChainGrpcTendermintApi } from '@shared/utils/lib/sdkImports'
import { fetchAccountDetailsUsingGrpc } from '@/app/services/account'
import { RFQ_GATEWAY_PREPARED_RESPONSE_MAX_AGE_MS } from '@/app/data/rfq'
import type { AuthBaseAccount } from '@injectivelabs/sdk-ts/client/chain'
import type {
  RfqGatewayStreamWarmupStatus
} from '@/composables/rfq/helpers/rfq/observability'
import type {
  RfqStreamEvent,
  RfqStreamFirmRequestParams
} from '@/composables/rfq/helpers/takerStream/streamClient'

export const RFQ_CLIENT_GATEWAY_TX_TIMEOUT_IN_BLOCKS = 10

export type RfqClientGatewayBroadcastContext = {
  fetchedAt: number
  latestHeight: string
  timeoutHeight: string
  accountDetails: AuthBaseAccount
}

type RfqClientGatewayBroadcastContextRequest =
  Promise<RfqClientGatewayBroadcastContext>

type RfqStreamClient = ReturnType<
  typeof import('@/composables/rfq/helpers/takerStream/streamClient')['createRfqStreamClient']
>
type RfqClientGatewayStreamWarmupResult = {
  status: RfqGatewayStreamWarmupStatus
}

let streamBroker:
  | undefined
  | ReturnType<typeof createRfqClientGatewayStreamBroker>
const broadcastContextCache: Record<
  string,
  undefined | RfqClientGatewayBroadcastContext
> = {}
const broadcastContextRequests: Record<
  string,
  undefined | RfqClientGatewayBroadcastContextRequest
> = {}

function isRecord(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === 'object'
}

function getLatestBlockHeight(latestBlock: unknown) {
  if (!isRecord(latestBlock) || !isRecord(latestBlock.header)) {
    throw new Error('Latest block response is missing header height')
  }

  const height = latestBlock.header.height

  if (typeof height === 'string') {
    return height
  }

  if (typeof height === 'bigint') {
    return height.toString()
  }

  if (typeof height === 'number' && Number.isInteger(height)) {
    return height.toString()
  }

  throw new Error('Latest block response has an invalid header height')
}

function getTimeoutHeight(params: {
  latestHeight: string
  txTimeoutInBlocks: number
}) {
  return (
    BigInt(params.latestHeight) + BigInt(params.txTimeoutInBlocks)
  ).toString()
}

function getTxTimeoutInBlocks(txTimeoutInBlocks?: number) {
  return txTimeoutInBlocks ?? RFQ_CLIENT_GATEWAY_TX_TIMEOUT_IN_BLOCKS
}

function getBroadcastContextCacheKey(params: {
  autosignAddress: string
  txTimeoutInBlocks?: number
}) {
  return `${params.autosignAddress}:${getTxTimeoutInBlocks(
    params.txTimeoutInBlocks
  )}`
}

function getBroadcastContextCacheKeyPrefix(autosignAddress: string) {
  return `${autosignAddress}:`
}

function getComparableAccountDetail(value: unknown) {
  if (typeof value === 'string') {
    return value
  }

  if (typeof value === 'number' && Number.isFinite(value)) {
    return value.toString()
  }

  if (typeof value === 'bigint') {
    return value.toString()
  }

  return ''
}

function isSameAccountDetails({
  left,
  right
}: {
  left: AuthBaseAccount
  right: AuthBaseAccount
}) {
  return (
    getComparableAccountDetail(left.sequence) ===
      getComparableAccountDetail(right.sequence) &&
    getComparableAccountDetail(left.accountNumber) ===
      getComparableAccountDetail(right.accountNumber)
  )
}

export function isRfqClientGatewayBroadcastContextFresh(
  context?: RfqClientGatewayBroadcastContext,
  now = Date.now()
): context is RfqClientGatewayBroadcastContext {
  if (!context) {
    return false
  }

  const contextAgeMs = now - context.fetchedAt

  return (
    contextAgeMs >= 0 &&
    contextAgeMs <= RFQ_GATEWAY_PREPARED_RESPONSE_MAX_AGE_MS
  )
}

async function fetchRfqClientGatewayBaseAccount(autosignAddress: string) {
  const accountDetails = await fetchAccountDetailsUsingGrpc(autosignAddress)
  const account = accountDetails.baseAccount

  if (!account) {
    throw new Error('RFQ client gateway account details missing base account')
  }

  return account
}

async function fetchBroadcastContext(params: {
  autosignAddress: string
  txTimeoutInBlocks?: number
  accountDetails?: AuthBaseAccount
}) {
  const txTimeoutInBlocks = getTxTimeoutInBlocks(params.txTimeoutInBlocks)
  const latestBlockPromise = getChainGrpcTendermintApi(ENDPOINTS.grpc).then(
    async (tendermintApi) => await tendermintApi.fetchLatestBlock()
  )
  const [account, latestBlock] = await Promise.all([
    params.accountDetails ||
      fetchRfqClientGatewayBaseAccount(params.autosignAddress),
    latestBlockPromise
  ])
  const latestHeight = getLatestBlockHeight(latestBlock)

  return {
    fetchedAt: Date.now(),
    latestHeight,
    accountDetails: account,
    timeoutHeight: getTimeoutHeight({ latestHeight, txTimeoutInBlocks })
  }
}

export async function getRfqClientGatewayBroadcastContext(params: {
  forceRefresh?: boolean
  autosignAddress: string
  txTimeoutInBlocks?: number
}) {
  const cacheKey = getBroadcastContextCacheKey(params)
  const cachedContext = broadcastContextCache[cacheKey]

  if (
    !params.forceRefresh &&
    isRfqClientGatewayBroadcastContextFresh(cachedContext)
  ) {
    return cachedContext
  }

  const activeRequest = broadcastContextRequests[cacheKey]

  if (!params.forceRefresh && activeRequest) {
    return await activeRequest
  }

  const request = fetchBroadcastContext(params).then((context) => {
    if (broadcastContextRequests[cacheKey] === request) {
      broadcastContextCache[cacheKey] = context
    }

    return context
  })
  const clearRequest = () => {
    if (broadcastContextRequests[cacheKey] === request) {
      broadcastContextRequests[cacheKey] = undefined
    }
  }

  broadcastContextRequests[cacheKey] = request
  request.then(clearRequest, clearRequest)

  return await request
}

export async function resolveRfqClientGatewayBroadcastContext(params: {
  autosignAddress: string
  txTimeoutInBlocks?: number
  shouldValidateAccountDetails?: boolean
  context?: RfqClientGatewayBroadcastContext
}) {
  const cacheKey = getBroadcastContextCacheKey(params)
  const context = isRfqClientGatewayBroadcastContextFresh(params.context)
    ? params.context
    : broadcastContextCache[cacheKey]

  if (isRfqClientGatewayBroadcastContextFresh(context)) {
    if (!params.shouldValidateAccountDetails) {
      return {
        context,
        isReused: true
      }
    }

    const accountDetails = await fetchRfqClientGatewayBaseAccount(
      params.autosignAddress
    )

    if (
      isSameAccountDetails({
        left: context.accountDetails,
        right: accountDetails
      })
    ) {
      return {
        context,
        isReused: true
      }
    }

    const refreshedContext = await fetchBroadcastContext({
      accountDetails,
      autosignAddress: params.autosignAddress,
      txTimeoutInBlocks: params.txTimeoutInBlocks
    })

    broadcastContextRequests[cacheKey] = undefined
    broadcastContextCache[cacheKey] = refreshedContext

    return {
      isReused: false,
      context: refreshedContext
    }
  }

  return {
    isReused: false,
    context: await getRfqClientGatewayBroadcastContext({
      forceRefresh: params.shouldValidateAccountDetails,
      autosignAddress: params.autosignAddress,
      txTimeoutInBlocks: params.txTimeoutInBlocks
    })
  }
}

export function invalidateRfqClientGatewayBroadcastContext(
  autosignAddress: string
) {
  const cacheKeyPrefix = getBroadcastContextCacheKeyPrefix(autosignAddress)

  Object.keys(broadcastContextCache).forEach((cacheKey) => {
    if (cacheKey.startsWith(cacheKeyPrefix)) {
      broadcastContextCache[cacheKey] = undefined
    }
  })
  Object.keys(broadcastContextRequests).forEach((cacheKey) => {
    if (cacheKey.startsWith(cacheKeyPrefix)) {
      broadcastContextRequests[cacheKey] = undefined
    }
  })
}

function createRfqClientGatewayStreamBroker() {
  const subscribers = new Set<(event: RfqStreamEvent) => void>()
  const streamUnsubscribes = new Map<
    (event: RfqStreamEvent) => void,
    () => void
  >()

  let streamClient: undefined | RfqStreamClient
  let streamClientPromise: undefined | Promise<RfqStreamClient>
  let connectedTakerAddress: string | undefined
  let connectingTakerAddress: string | undefined
  let streamClientGeneration = 0
  let connectPromise: undefined | Promise<RfqClientGatewayStreamWarmupResult>

  function attachSubscriber(callback: (event: RfqStreamEvent) => void) {
    if (!streamClient || streamUnsubscribes.has(callback)) {
      return
    }

    streamUnsubscribes.set(callback, streamClient.subscribe(callback))
  }

  function clearStreamClientSubscriptions() {
    streamUnsubscribes.forEach((unsubscribe) => unsubscribe())
    streamUnsubscribes.clear()
  }

  async function getStreamClient() {
    if (streamClient) {
      return streamClient
    }

    if (streamClientPromise) {
      return await streamClientPromise
    }

    const generation = streamClientGeneration
    const request = import(
      '@/composables/rfq/helpers/takerStream/streamClient'
    )
      .then(async ({ createRfqStreamClient }) => {
        const client = createRfqStreamClient()

        if (streamClientGeneration !== generation) {
          await client.disconnect()

          throw new Error('RFQ client gateway stream client was superseded')
        }

        streamClient = client
        subscribers.forEach(attachSubscriber)

        return client
      })
      .catch((error) => {
        if (
          streamClientPromise === request &&
          streamClientGeneration === generation
        ) {
          streamClientPromise = undefined
        }

        throw error
      })

    streamClientPromise = request

    return await request
  }

  async function resetStreamClient() {
    const currentStreamClient = streamClient

    streamClientGeneration += 1
    clearStreamClientSubscriptions()
    streamClient = undefined
    streamClientPromise = undefined

    await currentStreamClient?.disconnect()
  }

  async function connect(params: {
    takerAddress: string
  }): Promise<RfqClientGatewayStreamWarmupResult> {
    let activeStreamClient = await getStreamClient()
    let activeStreamClientGeneration = streamClientGeneration

    if (
      activeStreamClient.isConnected.value &&
      connectedTakerAddress === params.takerAddress
    ) {
      return { status: 'warm' }
    }

    if (connectPromise && connectingTakerAddress === params.takerAddress) {
      return await connectPromise
    }

    const isPendingConnectionSuperseded =
      !!connectPromise && connectingTakerAddress !== params.takerAddress
    const isReconnectRequired =
      !!connectedTakerAddress && connectedTakerAddress !== params.takerAddress

    if (isReconnectRequired || isPendingConnectionSuperseded) {
      await resetStreamClient()
      activeStreamClient = await getStreamClient()
      activeStreamClientGeneration = streamClientGeneration
    }

    connectingTakerAddress = params.takerAddress

    const connectingStreamClient = activeStreamClient
    const request: Promise<RfqClientGatewayStreamWarmupResult> =
      connectingStreamClient
      .connect({ requestAddress: params.takerAddress })
      .then((): RfqClientGatewayStreamWarmupResult => {
        const isCurrentRequest =
          streamClient === connectingStreamClient &&
          connectPromise === request &&
          streamClientGeneration === activeStreamClientGeneration

        if (!isCurrentRequest) {
          throw new Error('RFQ client gateway stream connection was superseded')
        }

        connectedTakerAddress = params.takerAddress

        return {
          status: isReconnectRequired ? 'reconnected' : 'connected'
        }
      })
      .catch((error) => {
        if (
          streamClient === connectingStreamClient &&
          connectedTakerAddress === params.takerAddress
        ) {
          connectedTakerAddress = undefined
        }

        throw error
      })

    connectPromise = request

    try {
      return await request
    } finally {
      if (connectPromise === request) {
        connectPromise = undefined
        connectingTakerAddress = undefined
      }
    }
  }

  async function disconnect() {
    const currentStreamClient = streamClient

    connectedTakerAddress = undefined
    connectingTakerAddress = undefined
    connectPromise = undefined
    streamClientGeneration += 1
    streamClient = undefined
    streamClientPromise = undefined

    clearStreamClientSubscriptions()
    subscribers.clear()

    await currentStreamClient?.disconnect()
  }

  return {
    connect,
    disconnect,
    subscribe(callback: (event: RfqStreamEvent) => void) {
      subscribers.add(callback)
      attachSubscriber(callback)

      return () => {
        subscribers.delete(callback)
        streamUnsubscribes.get(callback)?.()
        streamUnsubscribes.delete(callback)
      }
    },
    async sendFirmRequest(params: RfqStreamFirmRequestParams) {
      const client = await getStreamClient()

      return client.sendFirmRequest(params)
    }
  }
}

export function getRfqClientGatewayStreamBroker() {
  if (!streamBroker) {
    streamBroker = createRfqClientGatewayStreamBroker()
  }

  return streamBroker
}

export function warmRfqClientGatewayStream(params: { takerAddress: string }) {
  return getRfqClientGatewayStreamBroker().connect(params)
}

export async function disconnectRfqClientGatewayStream() {
  const broker = streamBroker

  streamBroker = undefined

  await broker?.disconnect()
}

export function resetRfqClientGatewayPrerequisitesForTests() {
  streamBroker = undefined

  Object.keys(broadcastContextCache).forEach((autosignAddress) => {
    broadcastContextCache[autosignAddress] = undefined
  })
  Object.keys(broadcastContextRequests).forEach((autosignAddress) => {
    broadcastContextRequests[autosignAddress] = undefined
  })
}
